import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Theme } from '../lib/tokens';
import { FONTS } from '../lib/tokens';
import { Icon } from '../components/icons';
import { PoweredByGoogle } from '../components/GoogleBrand';
import { SoftButton } from '../components/common';
import { useAuth } from '../hooks/useAuth';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8080';

interface Venue {
  id?: string;
  name: string;
  address: string;
  priceRange?: string;
  mapsUrl?: string;
}


interface DateSchedulerProps {
  matchId: string;
  datePlanId: string;
  otherUserName: string;
  venues: Venue[];
  groupSize: number;
  t: Theme;
  onClose: () => void;
  onProposed: () => void;
  initialVenueName?: string;
}

const TIME_SLOTS = [
  '17:00', '17:30', '18:00', '18:30',
  '19:00', '19:30', '20:00', '20:30',
  '21:00', '21:30',
];

function formatDate(d: Date): string {
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function getNext14Days(): Date[] {
  const days: Date[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (let i = 1; i <= 14; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    days.push(d);
  }
  return days;
}

export function DateScheduler({ matchId, venues, groupSize, otherUserName, t, onClose, onProposed, initialVenueName }: DateSchedulerProps) {
  const { getIdToken } = useAuth();
  const queryClient = useQueryClient();
  const [show, setShow] = useState(false);
  const [selectedVenueIdx, setSelectedVenueIdx] = useState(0);
  const [customVenue, setCustomVenue] = useState(initialVenueName ?? '');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [partySize, setPartySize] = useState(groupSize);
  const [showReservationNotice, setShowReservationNotice] = useState(false);

  useEffect(() => { const id = setTimeout(() => setShow(true), 40); return () => clearTimeout(id); }, []);

  const days = getNext14Days();
  const hasVenues = venues.length > 0;
  const selectedVenue = hasVenues ? venues[selectedVenueIdx] : null;
  const effectiveVenueName = hasVenues ? selectedVenue?.name : customVenue.trim();
  const canPropose = effectiveVenueName && selectedDate && selectedTime;

  const proposeMutation = useMutation({
    mutationFn: async () => {
      const token = await getIdToken();
      const res = await fetch(`${API_BASE}/api/matches/${matchId}/date-proposal`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          venueId: selectedVenue?.id || effectiveVenueName,
          venueName: effectiveVenueName,
          date: isoDate(selectedDate!),
          time: selectedTime!,
          partySize,
        }),
      });
      if (!res.ok) throw new Error('Failed to propose');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['matches'] });
      setShowReservationNotice(true);
    },
  });

  const PRICE_LABEL: Record<string, string> = {
    BUDGET: '£', MODERATE: '££', PREMIUM: '£££', LUXURY: '££££',
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'absolute', inset: 0, zIndex: 220,
        background: 'rgba(31,26,23,0.6)', backdropFilter: 'blur(8px)',
        display: 'flex', flexDirection: 'column',
        opacity: show ? 1 : 0, transition: 'opacity 200ms',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          marginTop: 80, background: t.paper, borderRadius: '28px 28px 0 0',
          flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden',
          transform: show ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 380ms cubic-bezier(0.2,0.8,0.2,1)',
        }}
      >
        {/* Handle + Header */}
        <div style={{ padding: '10px 20px 12px', flexShrink: 0 }}>
          <div style={{ width: 40, height: 4, borderRadius: 2, background: t.line, margin: '0 auto 14px' }} />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontFamily: FONTS.sans, fontSize: 10.5, letterSpacing: 1.5, textTransform: 'uppercase', color: t.inkMute }}>
                Propose a date
              </div>
              <div style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 22, color: t.ink, lineHeight: 1.1, marginTop: 2 }}>
                With {otherUserName}
              </div>
            </div>
            <button onClick={onClose} style={{
              all: 'unset', cursor: 'pointer',
              width: 34, height: 34, borderRadius: 100,
              background: t.card, border: `1px solid ${t.line}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>{Icon.x({ s: 16, c: t.ink })}</button>
          </div>
        </div>

        {/* Scrollable form */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 20px 140px' }} className="scroll-container">

          {/* Venue select */}
          <Section label="Pick a venue" t={t}>
            {hasVenues ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {venues.map((v, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedVenueIdx(i)}
                    style={{
                      all: 'unset', cursor: 'pointer',
                      padding: '12px 14px', borderRadius: 14,
                      background: selectedVenueIdx === i ? t.terracotta : t.card,
                      border: `1.5px solid ${selectedVenueIdx === i ? t.terracotta : t.line}`,
                      transition: 'all 160ms',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{
                        width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                        background: selectedVenueIdx === i ? t.paper : t.inkMute,
                      }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontFamily: FONTS.sans, fontSize: 14, fontWeight: 600,
                          color: selectedVenueIdx === i ? t.paper : t.ink,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>{v.name}</div>
                        <div style={{
                          fontFamily: FONTS.sans, fontSize: 11,
                          color: selectedVenueIdx === i ? 'rgba(245,237,225,0.7)' : t.inkMute,
                          marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {v.address}{v.priceRange ? ` · ${PRICE_LABEL[v.priceRange] || v.priceRange}` : ''}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
                {/* Places API policy: venue data shown without a Google Map
                    requires a visible "Powered by Google" credit. */}
                <PoweredByGoogle t={t} style={{ alignSelf: 'flex-end', marginTop: 2 }} />
              </div>
            ) : (
              <input
                type="text"
                placeholder="e.g. Dishoom Covent Garden"
                value={customVenue}
                onChange={e => setCustomVenue(e.target.value)}
                style={{
                  width: '100%', boxSizing: 'border-box',
                  padding: '13px 14px', borderRadius: 14,
                  fontFamily: FONTS.sans, fontSize: 14, color: t.ink,
                  background: t.card, border: `1.5px solid ${customVenue.trim() ? t.terracotta : t.line}`,
                  outline: 'none', transition: 'border-color 160ms',
                }}
              />
            )}
          </Section>

          {/* Date select */}
          <Section label="Choose a date" t={t}>
            <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4, marginLeft: -2, paddingLeft: 2 }} className="scroll-container">
              {days.map((d, i) => {
                const selected = selectedDate && isoDate(d) === isoDate(selectedDate);
                return (
                  <button
                    key={i}
                    onClick={() => setSelectedDate(d)}
                    style={{
                      all: 'unset', cursor: 'pointer', flexShrink: 0,
                      display: 'flex', flexDirection: 'column', alignItems: 'center',
                      padding: '10px 14px', borderRadius: 14,
                      background: selected ? t.terracotta : t.card,
                      border: `1.5px solid ${selected ? t.terracotta : t.line}`,
                      transition: 'all 160ms',
                    }}
                  >
                    <span style={{
                      fontFamily: FONTS.mono, fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.5,
                      color: selected ? 'rgba(245,237,225,0.75)' : t.inkMute, marginBottom: 2,
                    }}>
                      {d.toLocaleDateString('en-GB', { weekday: 'short' })}
                    </span>
                    <span style={{
                      fontFamily: FONTS.sans, fontSize: 17, fontWeight: 700,
                      color: selected ? t.paper : t.ink, lineHeight: 1,
                    }}>
                      {d.getDate()}
                    </span>
                    <span style={{
                      fontFamily: FONTS.mono, fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.5,
                      color: selected ? 'rgba(245,237,225,0.75)' : t.inkMute, marginTop: 1,
                    }}>
                      {d.toLocaleDateString('en-GB', { month: 'short' })}
                    </span>
                  </button>
                );
              })}
            </div>
          </Section>

          {/* Time select */}
          <Section label="Pick a time" t={t}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {TIME_SLOTS.map((slot) => {
                const selected = selectedTime === slot;
                return (
                  <button
                    key={slot}
                    onClick={() => setSelectedTime(slot)}
                    style={{
                      all: 'unset', cursor: 'pointer',
                      padding: '8px 16px', borderRadius: 100,
                      fontFamily: FONTS.mono, fontSize: 13, fontWeight: 500,
                      background: selected ? t.terracotta : t.card,
                      color: selected ? t.paper : t.ink,
                      border: `1.5px solid ${selected ? t.terracotta : t.line}`,
                      transition: 'all 160ms',
                    }}
                  >
                    {slot}
                  </button>
                );
              })}
            </div>
          </Section>

          {/* Party size */}
          <Section label="Party size" t={t}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <button
                onClick={() => setPartySize(s => Math.max(2, s - 1))}
                style={{
                  all: 'unset', cursor: 'pointer',
                  width: 36, height: 36, borderRadius: '50%',
                  background: t.card, border: `1px solid ${t.line}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: FONTS.sans, fontSize: 20, color: t.ink,
                }}
              >−</button>
              <div style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 28, color: t.ink, minWidth: 40, textAlign: 'center' }}>
                {partySize}
              </div>
              <button
                onClick={() => setPartySize(s => Math.min(8, s + 1))}
                style={{
                  all: 'unset', cursor: 'pointer',
                  width: 36, height: 36, borderRadius: '50%',
                  background: t.card, border: `1px solid ${t.line}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: FONTS.sans, fontSize: 20, color: t.ink,
                }}
              >+</button>
              <span style={{ fontFamily: FONTS.sans, fontSize: 13, color: t.inkMute }}>people</span>
            </div>
          </Section>

          {/* Summary preview */}
          {canPropose && (
            <div style={{
              padding: '14px 16px', borderRadius: 16,
              background: t.card, border: `1px solid ${t.line}`,
              marginTop: 4,
            }}>
              <div style={{ fontFamily: FONTS.sans, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: t.inkMute, marginBottom: 8 }}>
                Your proposal
              </div>
              <div style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 18, color: t.ink, marginBottom: 4 }}>
                {effectiveVenueName}
              </div>
              <div style={{ fontFamily: FONTS.sans, fontSize: 13, color: t.inkSoft }}>
                {formatDate(selectedDate!)} at {selectedTime} · {partySize} people
              </div>
            </div>
          )}
        </div>

        {/* Sticky CTA */}
        <div style={{
          position: 'absolute', left: 0, right: 0, bottom: 0, padding: '14px 20px 30px',
          background: `linear-gradient(to top, ${t.paper} 70%, transparent)`,
          display: 'flex', gap: 10,
        }}>
          <SoftButton t={t} variant="outline" onClick={onClose} style={{ flex: 1 }}>
            Back
          </SoftButton>
          <SoftButton
            t={t}
            variant={canPropose ? 'terracotta' : 'outline'}
            style={{ flex: 1.5, opacity: canPropose ? 1 : 0.4 }}
            icon={canPropose ? Icon.heart({ s: 16, c: t.paper, fill: t.paper, w: 0 }) : undefined}
            onClick={canPropose && !proposeMutation.isPending ? () => proposeMutation.mutate() : undefined}
          >
            {proposeMutation.isPending ? 'Sending...' : 'Propose this date'}
          </SoftButton>
        </div>

        {/* Reservation notice — shown after proposal is sent */}
        {showReservationNotice && (
          <div style={{
            position: 'absolute', inset: 0, zIndex: 230,
            background: 'rgba(31,26,23,0.6)', backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'flex-end',
          }}>
            <div style={{
              width: '100%', background: t.paper,
              borderRadius: '24px 24px 0 0',
              padding: '28px 24px 40px',
            }}>
              <div style={{
                width: 48, height: 48, borderRadius: '50%',
                background: 'rgba(200,85,61,0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 24, marginBottom: 16,
              }}>📋</div>
              <div style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 22, color: t.ink, marginBottom: 10, lineHeight: 1.15 }}>
                One thing to note
              </div>
              <div style={{ fontFamily: FONTS.sans, fontSize: 14, color: t.inkSoft, lineHeight: 1.6, marginBottom: 24 }}>
                This version doesn't have a reservation engine yet — we're working on it. If the venue requires a booking, please make sure to reserve it yourself.
              </div>
              <div style={{
                padding: '12px 14px', borderRadius: 12, marginBottom: 20,
                background: t.paperAlt, border: `1px solid ${t.line}`,
                fontFamily: FONTS.sans, fontSize: 12, color: t.inkMute, lineHeight: 1.5,
              }}>
                💡 Most London venues accept walk-ins for smaller groups, but it's always worth a quick call or OpenTable check.
              </div>
              <SoftButton t={t} variant="terracotta" style={{ width: '100%' }} onClick={onProposed}>
                Got it — let's go ✓
              </SoftButton>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ label, t, children }: { label: string; t: Theme; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{
        fontFamily: FONTS.sans, fontSize: 11, fontWeight: 600,
        textTransform: 'uppercase', letterSpacing: 1.2,
        color: t.inkMute, marginBottom: 10,
      }}>
        {label}
      </div>
      {children}
    </div>
  );
}
